import { useEffect, useMemo, useState } from "react";
import { adminService } from "../../../../services/admin-service";
import { AdminPageHeader, AdminPanel, EmptyState, StatusBadge } from "./admin-ui";

const groups = [
    {
        key: "general",
        title: "General",
        fields: [
            ["website_name", "Website Name"],
            ["support_email", "Support Email"],
            ["support_phone", "Support Phone"],
            ["office_location", "Office Location"],
            ["maintenance_message", "Maintenance Message"]
        ]
    },
    {
        key: "branding",
        title: "Branding",
        fields: [
            ["logo_url", "Logo URL"],
            ["favicon_url", "Favicon URL"],
            ["social_share_image", "Social Share Image"]
        ]
    },
    {
        key: "job_rules",
        title: "Job Rules",
        fields: [
            ["default_job_status", "Default Job Status"],
            ["blocked_words", "Blocked Words"],
            ["job_expiry_days", "Default Expiry Days"],
            ["review_sla_hours", "Review SLA Hours"]
        ]
    },
    {
        key: "uploads",
        title: "Uploads",
        fields: [
            ["max_image_mb", "Max Image MB"],
            ["max_resume_mb", "Max Resume MB"],
            ["allowed_resume_types", "Allowed Resume Types"]
        ]
    }
];

function AdminSettingsPage() {
    const [settings, setSettings] = useState({});
    const [flags, setFlags] = useState([]);
    const [message, setMessage] = useState("");

    const load = () => {
        Promise.all([adminService.settings(), adminService.featureFlags()])
            .then(([settingsData, flagsData]) => {
                setSettings(settingsData.settings || {});
                setFlags(flagsData.flags || []);
            })
            .catch((error) => setMessage(error.message));
    };

    useEffect(() => {
        load();
    }, []);

    const updateField = (group, key, value) => {
        setSettings((current) => ({
            ...current,
            [group]: { ...(current[group] || {}), [key]: value }
        }));
    };

    const saveGroup = async (group) => {
        try {
            await adminService.updateSettings(group, settings[group] || {});
            setMessage("Settings saved.");
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const toggleFlag = async (flag) => {
        try {
            await adminService.updateFeatureFlag(flag.flag_key, !flag.enabled);
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    return (
        <>
            <AdminPageHeader title="Advanced Settings" subtitle="Website, moderation, uploads, and feature flags" />
            {message && <div className="alert alert-info">{message}</div>}
            <div className="row">
                {groups.map((group) => (
                    <div className="col-xl-6 col-lg-12 mb-4" key={group.key}>
                        <AdminPanel title={`${group.title} Settings`} icon="fa fa-cog">
                            {group.fields.map(([key, label]) => (
                                <div className="m-b15" key={key}>
                                    <label className="form-label">{label}</label>
                                    <input className="form-control" value={settings[group.key]?.[key] || ""} onChange={(event) => updateField(group.key, key, event.target.value)} />
                                </div>
                            ))}
                            <button className="site-button" type="button" onClick={() => saveGroup(group.key)}>Save {group.title}</button>
                        </AdminPanel>
                    </div>
                ))}
            </div>
            <AdminPanel title="Feature Flags" icon="fa fa-toggle-on">
                <div className="table-responsive">
                    <table className="table table-bordered">
                        <thead><tr><th>Feature</th><th>Description</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                            {flags.length === 0 ? (
                                <tr><td colSpan="4"><EmptyState text="No feature flags found." /></td></tr>
                            ) : flags.map((flag) => (
                                <tr key={flag.flag_key}>
                                    <td>{flag.flag_key}</td>
                                    <td>{flag.description}</td>
                                    <td><StatusBadge status={flag.enabled ? "active" : "inactive"} /></td>
                                    <td><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => toggleFlag(flag)}>{flag.enabled ? "Disable" : "Enable"}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </AdminPanel>
        </>
    );
}

export function usePublicSettings(settings) {
    return useMemo(() => settings?.general || {}, [settings]);
}

export default AdminSettingsPage;
